<template>
  <div>
    <div class="text-center">
      <PageTitle title="My Rewards History" />
      <span
        v-if="offer.use_points && points.next_points_expiration.points"
        class="d-block text-body-2 mb-6 mt-n5"
      >
        {{ points.next_points_expiration.points }} points expire on:
        <span>
          {{ expirationDate }}
        </span>
      </span>
    </div>

    <v-expansion-panels
      v-if="deals.length > 1 && !offer.use_points"
      v-model="expansionPanel"
      class="px-4"
      flat
      variant="accordion"
    >
      <v-expansion-panel>
        <v-expansion-panel-title hide-actions class="d-flex justify-space-between align-center pa-0">
          <span class="flex-grow-1">When Do My Points Expire?</span>
          <span class="d-flex align-center shrink-0">
            <icon v-show="showExpirationPoints" height="15" width="15">
              angle-up
            </icon>
            <icon v-show="!showExpirationPoints" height="15" width="15">
              angle-down
            </icon>
          </span>
        </v-expansion-panel-title>
        <v-divider v-if="showExpirationPoints" class="mt-0"></v-divider>
        <v-expansion-panel-text class="pb-0">
          <div v-for="deal in deals" :key="deal.id" class="text-caption pa-0 pt-2">
            <div>
              <span class="font-weight-bold">
                {{ deal.name }}
              </span>
              points expire on:
              <span class="font-weight-bold">
                {{ $moment(deal.points_valid_until).format('MMM D, YYYY') }}
              </span>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div v-else-if="deals.length === 1 && !offer.use_points">
      <div
        v-for="deal in deals"
        :key="deal.id"
        class="text-caption pa-0 text-center"
      >
        <div>
          <span class="font-weight-bold">{{ deal.name }}</span>
          points expire on:
          <span class="font-weight-bold">
            {{ $moment(deal.points_valid_until).format('MMM D, YYYY') }}
          </span>
        </div>
      </div>
    </div>

    <v-alert v-if="pointHistory.length === 0" border="start" class="rewards-history-alert ma-4" type="info">
      <template #prepend>
        <div class="rewards-history-alert__icon d-flex align-center me-4">
          <icon fill="white" height="20" width="20">
            info-circle
          </icon>
        </div>
      </template>
      <span class="d-inline-block">You dont have any rewards history.</span>

      <span v-if="offer.use_points" class="ms-1">
        To see potential rewards click
        <router-link
          class="text-white"
          :to="{
            name: 'Rewards',
            query: {
              t: $route.query.t
            }
          }"
        >
          here
        </router-link>
        .
      </span>
    </v-alert>

    <div v-else class="pb-4">
      <v-card
        v-for="(history, idx) in pointHistory"
        :key="idx"
        class="history-card px-4"
        elevation="0"
        rounded="0"
      >
        <v-divider v-if="idx < pointHistory.length" class="my-4" />
        <div>
          <div v-if="2 === 1" class="text-caption font-weight-light mb-1">
            {{ history.points >= 0 ? 'Redemption' : 'Claimed Prize' }}
          </div>

          <div class="d-flex justify-space-between">
            <div class="d-flex flex-column">
              <span
                v-if="history.reason_text"
                class="text-subtitle-2 pr-7 font-weight-bold"
              >
                {{
                  history.reason_text.replace(/^./, str => str.toUpperCase())
                }}
              </span>
              <span class="text-caption">{{ history.dealName }}</span>
              <span class="text-caption font-weight-light">
                {{ historyDate(history) }}
              </span>
            </div>
            <div
              class="history-card__points d-flex flex-column align-self-start text-right"
            >
              <span class="text-subtitle-2 font-weight-bold">
                {{ history.points > 0 ? `+${history.points}` : history.points }}
                <span>pts</span>
              </span>
              <span class="text-caption font-weight-light">
                {{ history.balance }}
                <span>pts</span>
              </span>
              <v-btn
                v-if="historyRedeemed(history)"
                class="text-white mt-3 pending-pickup-btn"
                color="blue"
                rounded
                size="small"
                variant="flat"
                :to="{
                  name: 'OrderDetails',
                  params: {
                    orderId: historyRedeemedIdExists(history)
                  },
                  query: {
                    t: $route.query.t,
                    type: 'products'
                  }
                }"
              >
                Pending Pickup
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import PageTitle from '@/components/shared/PageTitle/index.vue';

export default {
  name: 'RewardsHistory',
  components: {
    PageTitle
  },
  data() {
    return {
      current_page: 1,
      emptyData: false,
      expansionPanel: undefined,
      histories: [],
      loading: false,
      points: {
        current_points: '',
        next_points_expiration: {
          date: '',
          points: ''
        }
      }
    };
  },
  computed: {
    ...mapGetters('deals', ['getGamifiedDealsWithOrders']),
    ...mapState(['offer', 'partnerUser']),

    deals() {
      return this.getGamifiedDealsWithOrders;
    },

    expirationDate() {
      return this.$getLocalDate(
        this.points.next_points_expiration.date,
        'MMM D, YYYY'
      );
    },

    pointHistory: {
      get() {
        return this.histories;
      },
      set(newV) {
        this.histories = newV;
      }
    },

    showExpirationPoints() {
      return this.expansionPanel === 0;
    }
  },
  async created() {
    if (this.offer.use_points > 0) this.getOrderHistoryByOfferId();
    else this.getOrderHistoryByDealIds();

    try {
      const { data: points } = await this.$api.orders.getPoints({
        partnerUser: this.partnerUser.id,
        params: {
          offerId: this.offer.id
        }
      });
      this.points = points;
    } catch (error) {
      console.error('Could not grab points for order:', error);
    }
  },
  methods: {
    async getOrderHistoryByDealIds() {
      const dealHistory = this.deals.map(async deal => {
        try {
          const { data: historyData } = await this.$api.orders.getPointsHistory(
            {
              partnerUser: this.partnerUser.id,
              params: {
                offerId: this.offer.id,
                dealId: deal.id,
                page: this.current_page,
                display: 1000000
              }
            }
          );

          const historyDataWithDealName = historyData.data.map(historyItem => ({
            ...historyItem,
            dealName: deal.name
          }));

          this.histories = [...this.histories, ...historyDataWithDealName];
        } catch (error) {
          console.error('Failed to grab the order History:', error);
        }
      });
      return dealHistory;
    },

    async getOrderHistoryByOfferId() {
      try {
        this.loading = true;
        const { data: historyData } = await this.$api.orders.getPointsHistory({
          partnerUser: this.partnerUser.id,
          params: {
            offerId: this.offer.id,
            page: this.current_page,
            display: 1000000
          }
        });

        this.histories = [...this.histories, ...historyData.data];
        this.emptyData = historyData.data.length === 0;

        this.loading = false;
      } catch (error) {
        console.error('Failed to grab the order History:', error);
      }
    },
    historyDate(history) {
      return this.$getLocalDate(history.value_date, 'MMM D, YYYY');
    },
    historyRedeemed(historyItem) {
      return (
        historyItem.redeemed?.completed === null &&
        historyItem.redeemed?.delivery_method === 'picked_up'
      );
    },
    historyRedeemedIdExists(historyItem) {
      return historyItem.redeemed?.id;
    }
  }
};
</script>

<style lang="scss" scoped>
.history-card {
  &__points {
    min-width: 100px;
  }

  &--bordered {
    border-top: solid 1px rgba(var(--v-theme-on-surface), 0.12);
    padding-top: 16px;
    margin-top: 0;
  }
}

.rewards-history-alert {
  :deep(.v-alert__content) {
    align-items: center;
  }

  &__icon {
    align-self: center;
  }
}

:deep(.v-expansion-panel-text__wrapper) {
  padding-bottom: 0 !important;
}

:deep(.v-divider) {
  border-color: rgba(var(--v-theme-on-surface), var(--v-divider-opacity));
}

:deep(.v-btn.v-btn--size-small) {
  --v-btn-size: 0.6875rem;
  --v-btn-height: 24px;

  font-size: var(--v-btn-size);
  min-width: 50px;
  padding: 0 10px;
}

// .pending-pickup-btn {
//   font-size: 0.6875rem !important;
// }
</style>
